/**
 * OpenWeatherMap 2.5 — current conditions + 5-day/3-hour forecast.
 * No React imports: URL building and response normalization are pure
 * functions, the fetch pair is a thin shell over `fetchJson`.
 *
 * Why 2.5 and not One Call 3.0: One Call is refused on a free key (verified
 * 401, "requires a separate subscription to the One Call by Call plan"), so
 * the free tier genuinely cannot serve alerts. See DECISIONS.md — alerts come
 * from api.weather.gov instead.
 *
 * All OWM responses, INCLUDING error bodies, carry `Access-Control-Allow-Origin: *`,
 * so an invalid key surfaces as a readable HTTP status rather than an opaque
 * CORS failure. That is why the error text here can be specific.
 *
 * Temperatures are requested in metric and converted, because the contract
 * requires both °C and °F on every reading and one request must serve both.
 */

import type { WeatherDay, WeatherNow } from '../../config/contracts';
import { compareDateKeys, localDateKey } from '../date';
import { fetchJson } from '../http';
import {
  cToF,
  conditionIcon,
  conditionLabel,
  owmIconIsDay,
  owmIdToCondition,
  sentenceCase,
  type ConditionCode,
} from './conditions';

const BASE = 'https://api.openweathermap.org/data/2.5';

/* ---------------------------- wire types ---------------------------- */
/* Every field optional: a provider is untrusted input, and `strict` plus
   noUncheckedIndexedAccess then force a guard at every access site. */

interface OwmCondition {
  readonly id?: number;
  readonly description?: string;
  readonly icon?: string;
}

interface OwmMain {
  readonly temp?: number;
  readonly feels_like?: number;
  readonly temp_min?: number;
  readonly temp_max?: number;
}

export interface OwmCurrentResponse {
  readonly weather?: readonly OwmCondition[];
  readonly main?: OwmMain;
  readonly dt?: number;
}

interface OwmForecastItem {
  readonly dt?: number;
  readonly main?: OwmMain;
  readonly weather?: readonly OwmCondition[];
}

export interface OwmForecastResponse {
  readonly list?: readonly OwmForecastItem[];
}

/* ------------------------------- URLs ------------------------------- */

export function currentUrl(lat: number, lon: number, apiKey: string): string {
  const q = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    units: 'metric',
    appid: apiKey,
  });
  return `${BASE}/weather?${q.toString()}`;
}

export function forecastUrl(lat: number, lon: number, apiKey: string): string {
  const q = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    units: 'metric',
    appid: apiKey,
  });
  return `${BASE}/forecast?${q.toString()}`;
}

/* --------------------------- normalization -------------------------- */

export function normalizeCurrent(
  raw: OwmCurrentResponse,
  locationName: string,
): WeatherNow | null {
  const tempC = raw.main?.temp;
  if (typeof tempC !== 'number' || !Number.isFinite(tempC)) return null;

  const first = raw.weather?.[0];
  const code: ConditionCode = owmIdToCondition(first?.id ?? Number.NaN);
  const isDay = owmIconIsDay(first?.icon);
  const feelsC = typeof raw.main?.feels_like === 'number' ? raw.main.feels_like : tempC;
  const description = first?.description;

  return {
    tempC,
    tempF: cToF(tempC),
    feelsLikeC: feelsC,
    feelsLikeF: cToF(feelsC),
    conditionCode: code,
    // OWM's own description ("light intensity drizzle") is more informative
    // than our canonical label, so prefer it and fall back to the label.
    conditionLabel:
      typeof description === 'string' && description.trim() !== ''
        ? sentenceCase(description)
        : conditionLabel(code),
    icon: conditionIcon(code, isDay),
    locationName,
  };
}

interface DayBucket {
  minC: number;
  maxC: number;
  /** Hours from local noon for the sample that currently owns the icon. */
  noonDistance: number;
  code: ConditionCode;
}

/**
 * Collapse 3-hourly samples into local calendar days.
 *
 * Grouping is by BROWSER-local day via `localDateKey`, deliberately: every
 * other date in this app (the daily reset, the top bar, the calendar) is
 * browser-local, and a forecast strip whose "Today" disagreed with the top
 * bar's date would be a worse bug than a boundary being an hour off for a
 * location in another timezone.
 *
 * The representative icon for a day is the sample nearest local noon, not the
 * first or the modal one: a day is named for its afternoon, and the first
 * sample of "tomorrow" is 3am.
 */
export function groupForecast(
  items: readonly OwmForecastItem[],
  maxDays: number = 5,
): WeatherDay[] {
  const buckets = new Map<string, DayBucket>();

  for (const item of items) {
    if (typeof item.dt !== 'number' || !Number.isFinite(item.dt)) continue;
    const at = new Date(item.dt * 1000);
    if (Number.isNaN(at.getTime())) continue;

    const temp = item.main?.temp;
    const lo = item.main?.temp_min ?? temp;
    const hi = item.main?.temp_max ?? temp;
    if (typeof lo !== 'number' || typeof hi !== 'number') continue;

    const key = localDateKey(at);
    const rawId = item.weather?.[0]?.id;
    const code = owmIdToCondition(typeof rawId === 'number' ? rawId : Number.NaN);
    const noonDistance = Math.abs(at.getHours() - 12);

    const existing = buckets.get(key);
    if (existing === undefined) {
      buckets.set(key, { minC: lo, maxC: hi, noonDistance, code });
      continue;
    }
    existing.minC = Math.min(existing.minC, lo);
    existing.maxC = Math.max(existing.maxC, hi);
    if (noonDistance < existing.noonDistance) {
      existing.noonDistance = noonDistance;
      existing.code = code;
    }
  }

  return [...buckets.entries()]
    .sort(([a], [b]) => compareDateKeys(a, b))
    .slice(0, maxDays)
    .map(([dateKey, b]) => toDay(dateKey, b.minC, b.maxC, b.code));
}

/** Shared by both providers so a day row is built exactly one way. */
export function toDay(
  dateKey: string,
  lowC: number,
  highC: number,
  code: ConditionCode,
): WeatherDay {
  return {
    dateKey,
    lowC,
    highC,
    lowF: cToF(lowC),
    highF: cToF(highC),
    conditionCode: code,
    // Forecast rows are whole days, so always the daytime glyph.
    icon: conditionIcon(code, true),
  };
}

/* ------------------------------- fetch ------------------------------ */

export interface ConditionsResult {
  readonly now: WeatherNow;
  readonly forecast: readonly WeatherDay[];
}

export type ProviderOutcome =
  | { readonly ok: true; readonly value: ConditionsResult }
  | { readonly ok: false; readonly error: string };

/** Never throws — `fetchJson` never rejects and nothing here can. */
export async function fetchOpenWeather(
  lat: number,
  lon: number,
  apiKey: string,
  locationName: string,
): Promise<ProviderOutcome> {
  const [current, forecast] = await Promise.all([
    fetchJson<OwmCurrentResponse>(currentUrl(lat, lon, apiKey)),
    fetchJson<OwmForecastResponse>(forecastUrl(lat, lon, apiKey)),
  ]);

  if (!current.ok) return { ok: false, error: describeOwmError(current.status, current.error) };

  const now = normalizeCurrent(current.data, locationName);
  if (now === null) return { ok: false, error: 'OpenWeatherMap returned no temperature.' };

  // A forecast failure is survivable — current conditions alone still render.
  const days = forecast.ok ? groupForecast(forecast.data.list ?? []) : [];
  return { ok: true, value: { now, forecast: days } };
}

export function describeOwmError(status: number | null, fallback: string): string {
  if (status === 401) return 'OpenWeatherMap rejected the API key.';
  if (status === 429) return 'OpenWeatherMap rate limit reached.';
  if (status === 404) return 'OpenWeatherMap has no data for these coordinates.';
  return fallback;
}
